import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArrangellyBrand from '@/components/brand/ArrangellyBrand';
import LegalLinks from '@/components/legal/LegalLinks';
import { LEGAL_ENTITY } from '@/constants/legalEntity';

const Footer: React.FC = () => {
  const quickLinks = [
    { name: 'Features', path: '/features' },
    { name: 'How it works', path: '/how-it-works' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="relative bg-slate-50 border-t border-slate-200/90 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="inline-block mb-6 group hover:opacity-95 transition-opacity">
              <ArrangellyBrand size="md" />
            </Link>
            <p className="text-slate-600 text-sm mb-2 leading-relaxed">
              Empowering music educators with premium tools and beautiful design.
            </p>
            <p className="text-slate-500 text-xs mb-6">
              {LEGAL_ENTITY.legalNameEn}
              <br />
              {LEGAL_ENTITY.country}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-8 h-8 rounded-full bg-white border border-slate-200 hover:bg-primary hover:text-primary-foreground flex items-center justify-center text-slate-500 transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-slate-900 font-semibold mb-6">Platform</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-primary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-slate-900 font-semibold mb-6">Legal</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/terms-and-conditions" className="text-slate-600 hover:text-primary transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-slate-600 hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-slate-600 hover:text-primary transition-colors">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-slate-600 hover:text-primary transition-colors">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-slate-900 font-semibold mb-6">Stay Updated</h3>
            <div className="flex flex-col gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              <Button className="bg-primary hover:bg-primary/90 text-white w-full">Subscribe</Button>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 mt-12 pt-8 space-y-4">
          <LegalLinks className="justify-center md:justify-start text-xs" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {LEGAL_ENTITY.productName} · {LEGAL_ENTITY.legalNameEn}. All rights
              reserved.
            </p>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Mail size={14} />
              <a href={`mailto:${LEGAL_ENTITY.contactEmail}`} className="hover:text-primary">
                {LEGAL_ENTITY.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
